import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import useU2000AxiosAuthenticated from "../u2000Core/axios/U2000UseAxiosAuthenticated";
import { U2000HandleApiError } from "../u2000Core/axios/U2000HandleApiError";
import { extractBaseLanguage } from "../u2000Core/tools/U2000LanguageUtils";
import { useToast } from "../u2000Core/context/toast/U2000ToastContext";
import type { U2000AgingBucket } from "../u2000Core/constants/U2000AgingBuckets";
import { NS } from "./U3134";
import {
  doGetTecAgeReport,
  doGetTecAgeSummary,
  luHandleAppiError,
  parseU3134TecAgeReportResponse,
  parseU3134TecAgeSummaryResponse,
  u3134BuildTecAgeRequestBody,
  u3134ReportToSummary,
  u3134TecAgeCacheKey,
  type U3134TecAgeReport,
  type U3134TecAgeSummaryReport,
} from "./U3134Service";

function isAxiosNotFound(error: unknown): boolean {
  return (
    typeof error === "object" &&
    error != null &&
    "response" in error &&
    (error as { response?: { status?: number } }).response?.status === 404
  );
}

const fullReportFallbackCache = new Map<string, U3134TecAgeReport>();

export function u3134ClearTecAgeFullReportFallbackCache(): void {
  fullReportFallbackCache.clear();
}

export function useU3134TecAgeSummaryHandlers() {
  const axios = useU2000AxiosAuthenticated();
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const [summary, setSummary] = useState<U3134TecAgeSummaryReport | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const lastRequestKeyRef = useRef<string>("");

  const loadSummary = useCallback(
    async (
      asOfDate: string,
      bucket?: U2000AgingBucket | null,
    ): Promise<U3134TecAgeSummaryReport | null> => {
      const trimmed = asOfDate?.trim().slice(0, 10);
      if (!trimmed || trimmed.length < 10) return null;
      const requestKey = u3134TecAgeCacheKey(trimmed, bucket);
      lastRequestKeyRef.current = requestKey;
      setIsLoading(true);
      try {
        const lang = extractBaseLanguage(i18n.language);
        const body = u3134BuildTecAgeRequestBody({ asOfDate: trimmed, lang, bucket });
        let parsed: U3134TecAgeSummaryReport | null = null;
        try {
          const res = await doGetTecAgeSummary(axios, body);
          parsed = parseU3134TecAgeSummaryResponse(res.data);
        } catch (error: unknown) {
          if (!isAxiosNotFound(error)) throw error;
          const fallbackKey = u3134TecAgeCacheKey(trimmed, bucket);
          let fullReport = fullReportFallbackCache.get(fallbackKey);
          if (!fullReport) {
            const res = await doGetTecAgeReport(axios, body);
            fullReport = parseU3134TecAgeReportResponse(res.data) ?? undefined;
            if (fullReport) fullReportFallbackCache.set(fallbackKey, fullReport);
          }
          parsed = fullReport ? u3134ReportToSummary(fullReport, bucket) : null;
        }
        if (lastRequestKeyRef.current === requestKey) {
          setSummary(parsed);
        }
        return parsed;
      } catch (error: unknown) {
        if (lastRequestKeyRef.current === requestKey) {
          setSummary(null);
        }
        const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
        return null;
      } finally {
        if (lastRequestKeyRef.current === requestKey) {
          setIsLoading(false);
        }
      }
    },
    [axios, i18n.language, t, toast],
  );

  const clearSummary = useCallback(() => {
    setSummary(null);
  }, []);

  return { summary, isLoading, loadSummary, clearSummary, setSummary };
}
